import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Newspaper, Plane, Building2, X } from "lucide-react";
import { reverseGeocode } from "../lib/geo";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Weather from "./Weather";
import CityPicker from "./travel/CityPicker";
import { LocationCascade } from "./LocationCascade";
import TranslatePage from "./TranslatePage";

const KEY = "aa_location";
const OFF_KEY = "aa_location_off"; // user explicitly disabled location-based content

const addStateCode = async (location) => {
  if (!location?.state || location.state_code) return location;
  if (location.lat != null && location.lon != null) {
    try {
      const resolved = await reverseGeocode(location.lat, location.lon);
      if (resolved.state_code) return { ...location, state_code: resolved.state_code };
    } catch {}
  }
  return location;
};

export default function LocationBar() {
  const { user } = useAuth();
  const [loc, setLoc] = useState(null);
  const [changing, setChanging] = useState(false);
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(OFF_KEY) === "1") { setAsked(true); return; } // user opted out
    const persist = (value) => { setLoc(value); localStorage.setItem(KEY, JSON.stringify(value)); };
    const saved = localStorage.getItem(KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setLoc(parsed);
        setAsked(true);
        addStateCode(parsed).then(persist);
        return;
      } catch {}
    }
    // Silent, permission-free auto-detect from the visitor's IP
    api.get("/geo/ip-lookup").then(async (r) => {
      if (r.data?.found) {
        persist(await addStateCode({ ...r.data, found: undefined, source: "auto" }));
        setAsked(true);
        return;
      }
      throw new Error("no ip fix");
    }).catch(() => {
      // fallback: browser geolocation (asks permission), else show "Set location"
      if (!navigator.geolocation) { setAsked(true); return; }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const resolved = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
            persist(await addStateCode({ ...resolved, source: "auto" }));
          } catch { setAsked(true); }
        },
        () => setAsked(true),
        { timeout: 8000, maximumAge: 600000 }
      );
    });
  }, []);

  // Signed-in users: a manually chosen location saved on the account is permanent and wins.
  useEffect(() => {
    if (!user || localStorage.getItem(OFF_KEY) === "1") return;
    api.get("/geo/my-location").then(({ data }) => {
      if (data?.location?.city || data?.location?.country) {
        const server = { ...data.location, source: "manual" };
        setLoc(server);
        localStorage.setItem(KEY, JSON.stringify(server));
        setAsked(true);
      } else {
        // first sign-in on this device: push an earlier local manual choice up to the account
        try {
          const local = JSON.parse(localStorage.getItem(KEY) || "null");
          if (local?.source === "manual") api.put("/geo/my-location", local).catch(() => {});
        } catch {}
      }
    }).catch(() => {});
  }, [user]);

  const save = async (value) => {
    localStorage.removeItem(OFF_KEY);
    const resolved = await addStateCode({ ...value, source: "manual" });
    setLoc(resolved);
    localStorage.setItem(KEY, JSON.stringify(resolved));
    setChanging(false);
    setAsked(true);
    if (user) api.put("/geo/my-location", resolved).catch(() => {});
  };
  const onPick = (r) => save({ city: r.name, state: r.admin1, country: r.country, country_code: r.country_code, lat: r.lat, lon: r.lon });
  const disable = () => {
    localStorage.removeItem(KEY);
    localStorage.setItem(OFF_KEY, "1");
    setLoc(null);
    setChanging(false);
    setAsked(true);
    if (user) api.delete("/geo/my-location").catch(() => {});
  };

  const today = new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  const cityHref = loc?.city
    ? `/cities/${encodeURIComponent(loc.city)}${loc.country_code ? `?country=${loc.country_code}` : ""}${loc.state_code ? `${loc.country_code ? "&" : "?"}region=${loc.state_code}` : ""}`
    : "#";

  if (!loc && !asked) return null; // still resolving

  return (
    <div className="bg-zinc-50 text-zinc-700 text-sm border-b border-zinc-200" data-testid="location-bar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-2 flex flex-wrap items-center gap-x-5 gap-y-1">
        {loc ? (
          <>
            <span className="flex flex-wrap items-center gap-1.5 font-500" data-testid="location-label">
              <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
              <span className="text-zinc-500">Showing information for</span>
              {loc.city ? <Link to={cityHref} data-testid="location-link-city" className="font-700 text-zinc-800 hover:text-blue-700 underline decoration-zinc-300 underline-offset-2 transition-colors">{loc.city}</Link> : "Unknown"}
              {loc.district && loc.district !== loc.city && <><span className="text-zinc-400">·</span><span data-testid="location-district-label">{loc.district}</span></>}
              {loc.state && <><span className="text-zinc-400">·</span>{loc.state_code
                ? <Link to={`/regions/${loc.state_code}`} data-testid="location-link-state" className="hover:text-blue-700 underline decoration-zinc-300 underline-offset-2 transition-colors">{loc.state}</Link>
                : <span data-testid="location-state-label">{loc.state}</span>}</>}
              {loc.country && <><span className="text-zinc-400">·</span>{loc.country_code
                ? <Link to={`/countries/${loc.country_code}`} data-testid="location-link-country" className="hover:text-blue-700 underline decoration-zinc-300 underline-offset-2 transition-colors">{loc.country}</Link>
                : <span data-testid="location-country-label">{loc.country}</span>}</>}
              {loc.source === "auto" && <span className="text-[10px] text-zinc-500 border border-zinc-300 bg-white px-1 py-px rounded" data-testid="location-auto-badge" title="Detected from your network — use Change to correct it">auto-detected</span>}
            </span>
            <button onClick={() => setChanging((c) => !c)} data-testid="location-change" className="text-blue-700 hover:underline text-xs font-600">Change ›</button>
            <button onClick={disable} data-testid="location-disable" title="Stop showing location-based content" className="text-zinc-500 hover:text-zinc-800 text-xs underline decoration-zinc-300 underline-offset-2">Turn off</button>
            <span className="text-zinc-400 hidden sm:inline">{today}</span>
            {loc.lat != null && <span className="text-zinc-700 hidden md:inline"><Weather lat={loc.lat} lon={loc.lon} compact /></span>}
            <div className="flex flex-wrap items-center gap-3 ml-auto max-w-full min-w-0 text-xs">
              <TranslatePage />
              <Link to={`/airports${loc.country_code ? `?country=${loc.country_code}` : ""}`} data-testid="location-airports" className="hidden sm:flex items-center gap-1 text-zinc-600 hover:text-blue-700 transition-colors"><Building2 className="w-3.5 h-3.5" /> Airports</Link>
              <Link to={`/airlines${loc.country ? `?q=${encodeURIComponent(loc.country)}` : ""}`} data-testid="location-airlines" className="hidden sm:flex items-center gap-1 text-zinc-600 hover:text-blue-700 transition-colors"><Plane className="w-3.5 h-3.5 -rotate-45" /> Airlines</Link>
              <Link to="/faq" data-testid="location-support" className="hidden md:flex items-center gap-1 text-zinc-600 hover:text-blue-700 transition-colors">Support</Link>
              <Link to="/get-app" data-testid="location-get-app" className="flex items-center gap-1 text-zinc-600 hover:text-blue-700 transition-colors">📱 Get the app</Link>
            </div>
          </>
        ) : (
          <>
            <span className="flex items-center gap-1.5 text-zinc-500"><MapPin className="w-4 h-4 text-blue-600" /> {localStorage.getItem(OFF_KEY) === "1" ? "Location-based content is off" : "Set your location for local airports, weather & news"}</span>
            <button onClick={() => setChanging(true)} data-testid="location-set" className="text-blue-700 hover:underline text-xs font-600">Set location ›</button>
            <div className="flex items-center gap-3 ml-auto text-xs">
              <TranslatePage />
              <Link to="/get-app" data-testid="location-get-app" className="flex items-center gap-1 text-zinc-600 hover:text-blue-700 transition-colors">📱 Get the app</Link>
            </div>
          </>
        )}
      </div>
      {changing && (
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-3">
          <div className="max-w-md bg-white text-foreground p-4 border border-zinc-300 rounded-lg relative shadow-sm">
            <button onClick={() => setChanging(false)} data-testid="location-picker-close" className="absolute top-2 right-2 text-muted-foreground"><X className="w-4 h-4" /></button>
            <div className="mb-3">
              <CityPicker autoFocus placeholder="Quick search: type your city…" onSelect={onPick} />
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="flex-1 border-t border-zinc-200" />
              <span className="text-[10px] uppercase font-600 text-zinc-400">or browse step by step</span>
              <span className="flex-1 border-t border-zinc-200" />
            </div>
            <LocationCascade initial={loc} onClose={() => setChanging(false)}
              onSave={(value, opts) => {
                save(value);
                if (opts?.keepOpen) setChanging(true);
              }} />
          </div>
        </div>
      )}
    </div>
  );
}
